import { Segmented } from '@crm/ui';
import type { LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export interface TabPage {
  key: string;
  label: string;
  icon: LucideIcon;
  render: () => React.ReactNode;
}

interface TabPagesProps {
  /** Nome do grupo para leitores de tela. */
  label: string;
  pages: TabPage[];
  /** Parametro da URL que guarda a aba aberta - ignorado quando basePath e usado. */
  param?: string;
  /**
   * Usa a propria URL como aba (`${basePath}/${key}`) em vez de "?aba=".
   * A rota precisa aceitar `${basePath}` e `${basePath}/:tab` apontando
   * para a mesma pagina. A aba padrao tambem ganha o proprio segmento -
   * o endereco nunca fica pela metade.
   */
  basePath?: string;
}

/**
 * Abas de pagina com a escolha na URL: a aba aberta sobrevive ao F5 e pode
 * ser mandada por link.
 */
export function TabPages({ label, pages, param = 'aba', basePath }: TabPagesProps) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const primeira = pages[0];

  const atual = basePath
    ? location.pathname.slice(basePath.length).replace(/^\//, '') || null
    : params.get(param);

  useEffect(() => {
    if (basePath && primeira && atual === null) {
      navigate(`${basePath}/${primeira.key}`, { replace: true });
    }
  }, [basePath, primeira, atual, navigate]);

  if (!primeira) return null;

  const pagina = pages.find((page) => page.key === atual) ?? primeira;

  function irPara(proxima: string) {
    if (basePath) {
      navigate(`${basePath}/${proxima}`, { replace: true });
    } else {
      setParams(proxima === primeira?.key ? {} : { [param]: proxima });
    }
  }

  return (
    <div className="space-y-4">
      <Segmented
        label={label}
        size="lg"
        options={pages.map((page) => ({ value: page.key, label: page.label, icon: page.icon }))}
        value={pagina.key}
        onChange={irPara}
      />

      {pagina.render()}
    </div>
  );
}
